// Las filas del paso de cliente (#98 de fct-ferias): la ficha a la izquierda, el cliente de Gescom a la
// derecha, campo a campo, y lo que manda un guardado. Separado de la pantalla para probarlo por sí solo.
import type { ComparacionCliente, SeleccionPerfil, ValorCampo } from '../../api/feriasApi';

/** Una fila que la columna de Gescom puede editar: un campo que tiene el cliente de Gescom (diseño M4 §2). */
export interface Fila {
  clave: string;
  etiqueta: string;
  mono?: boolean;
}

/** Las filas editables, en el orden de la ficha. */
export const FILAS: Fila[] = [
  { clave: 'nombre', etiqueta: 'Nombre' },
  { clave: 'apellido1', etiqueta: 'Primer apellido' },
  { clave: 'apellido2', etiqueta: 'Segundo apellido' },
  { clave: 'email', etiqueta: 'Email' },
  { clave: 'movil', etiqueta: 'Móvil', mono: true },
  { clave: 'nacimiento', etiqueta: 'Nacimiento', mono: true },
  { clave: 'cp_domicilio', etiqueta: 'CP del domicilio', mono: true },
];

/** La farmacia y las relaciones: se muestran de la ficha, y nunca se editan ni se guardan (fuente §3). */
export const FILAS_INFORMATIVAS: Fila[] = [
  { clave: 'farmacia_calle', etiqueta: 'Calle de la farmacia' },
  { clave: 'farmacia_poblacion', etiqueta: 'Población de la farmacia' },
  { clave: 'farmacia_cp', etiqueta: 'CP de la farmacia', mono: true },
  {
    clave: 'farmacia_telefono',
    etiqueta: 'Teléfono de la farmacia',
    mono: true,
  },
  { clave: 'relacionadas', etiqueta: 'Personas o entidades relacionadas' },
];

/** Los valores de la columna de Gescom, por clave de campo. */
export type Valores = Record<string, string>;

/** El mínimo de un cliente nuevo (D37): lo que falta, como lo nombra la pantalla. */
export function faltanParaCrear(derecha: Valores): string[] {
  const faltan: string[] = [];
  if (!lleno(derecha.nombre)) {
    faltan.push('Nombre');
  }
  if (!lleno(derecha.apellido1)) {
    faltan.push('Primer apellido');
  }
  if (!lleno(derecha.cp_domicilio)) {
    faltan.push('CP del domicilio');
  }
  if (!lleno(derecha.email) && !lleno(derecha.movil)) {
    faltan.push('Email o móvil');
  }
  return faltan;
}

/** Las claves que faltan según la API (CA07, D37), como las nombra la pantalla. */
export function nombreFaltante(clave: string): string {
  if (clave === 'contacto') {
    return 'Email o móvil';
  }
  return FILAS.find((f) => f.clave === clave)?.etiqueta ?? clave;
}

const lleno = (v: string | undefined) => (v ?? '').trim() !== '';

/** Un valor de la ficha como texto: una lista unida, las relaciones como «persona (tipo)»; vacío si no hay. */
export function texto(valor: ValorCampo | undefined): string {
  if (Array.isArray(valor)) {
    return valor
      .map((v) =>
        typeof v === 'string'
          ? v
          : v.tipo
            ? `${v.persona} (${v.tipo})`
            : v.persona,
      )
      .join(', ');
  }
  return typeof valor === 'string' ? valor.trim() : '';
}

/** El CL de la ficha como lo escribe Gescom: «79607» o «cl 79607» es «CL79607»; null si no hay. */
export function codigoCl(valor: ValorCampo | undefined): string | null {
  const compacto = texto(valor).replace(/\s+/g, '');
  if (compacto === '') {
    return null;
  }
  const digitos = /^(?:CL)?(\d+)$/i.exec(compacto);
  return digitos ? `CL${digitos[1]}` : compacto;
}

/**
 * Los valores que tiene Gescom para un campo, uno por móvil o código postal. Los móviles, el principal
 * primero: el que sustituye un móvil distinto (D38).
 */
export function valoresActuales(actual: string | null): string[] {
  return (actual ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');
}

const normal = (v: string) =>
  v.trim().toLocaleLowerCase('es').normalize('NFD').replace(/[̀-ͯ]/g, '');

// Un año de cuatro cifras en un nacimiento escrito de cualquier forma.
const anio = (v: string) => /(?<!\d)\d{4}(?!\d)/.exec(v)?.[0] ?? null;

/**
 * Si dos valores de un campo dicen lo mismo, sin mirar mayúsculas, acentos ni espacios; un nacimiento por
 * su año, porque Gescom solo guarda el año (diseño M4 §2).
 */
export function mismoValor(clave: string, a: string, b: string): boolean {
  if (clave === 'nacimiento') {
    return anio(a) !== null && anio(a) === anio(b);
  }
  return normal(a) === normal(b);
}

/** La columna de Gescom de un cliente cargado: sus valores actuales. */
export function derechaDe(
  comparacion: ComparacionCliente,
  ficha: Record<string, ValorCampo>,
): Valores {
  const derecha: Valores = {};
  FILAS.forEach(({ clave }) => {
    const actuales = valoresActuales(
      comparacion.campos.find((f) => f.clave === clave)?.actual ?? null,
    );
    // El móvil principal, que sustituye uno distinto (D38); de varios códigos postales, el de la ficha si
    // Gescom lo tiene, y si no el primero.
    const deLaFicha = texto(ficha[clave]);
    derecha[clave] =
      clave === 'movil'
        ? (actuales[0] ?? '')
        : (actuales.find((v) => mismoValor(clave, v, deLaFicha)) ??
          actuales[0] ??
          '');
  });
  return derecha;
}

/** La columna de Gescom de un cliente nuevo: los valores de la ficha. */
export function derechaNueva(ficha: Record<string, ValorCampo>): Valores {
  return Object.fromEntries(
    FILAS.map(({ clave }) => [clave, texto(ficha[clave])]),
  );
}

/** Un cambio que hará la actualización en un campo: su valor ahora y el que se manda. */
export interface Cambio {
  clave: string;
  antes: string;
  despues: string;
}

/**
 * Lo que manda la actualización: los valores de la columna de Gescom que difieren de los actuales, y
 * ninguno vacío, porque un valor vacío no borra nada.
 */
export function cambios(
  comparacion: ComparacionCliente,
  derecha: Valores,
): Cambio[] {
  return FILAS.flatMap(({ clave }) => {
    const despues = (derecha[clave] ?? '').trim();
    const actual =
      comparacion.campos.find((f) => f.clave === clave)?.actual ?? null;
    // El móvil frente al principal, al que sustituye (D38); un código postal frente a cualquiera.
    const actuales = valoresActuales(actual);
    const comparados = clave === 'movil' ? actuales.slice(0, 1) : actuales;
    if (
      despues === '' ||
      comparados.some((v) => mismoValor(clave, v, despues))
    ) {
      return [];
    }
    const antes = clave === 'movil' ? (actuales[0] ?? '') : (actual ?? '');
    return [{ clave, antes, despues }];
  });
}

/** Los campos vacíos en la columna de Gescom que Gescom sí tiene: se quedan como están. */
export function vaciosQueNoBorran(
  comparacion: ComparacionCliente,
  derecha: Valores,
): string[] {
  return FILAS.filter(
    ({ clave }) =>
      (derecha[clave] ?? '').trim() === '' &&
      (comparacion.campos.find((f) => f.clave === clave)?.actual ?? '') !== '',
  ).map((f) => f.etiqueta);
}

const ordenados = (ids: number[]) => [...ids].sort((a, b) => a - b).join();

/** Si dos perfiles de compra son la misma selección. */
export function mismoPerfil(a: SeleccionPerfil, b: SeleccionPerfil): boolean {
  return (
    a.plazo === b.plazo &&
    ordenados(a.tramos) === ordenados(b.tramos) &&
    ordenados(a.provincias) === ordenados(b.provincias)
  );
}
